import React, { useReducer, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useContextSelector, createContext } from "use-context-selector";

import {
  SocketMessageInterface,
  HallCallsState,
  TerminalRowInterface,
} from "../../../interfaces/Monitoring.interface";
import PositionCallsStatusT from "../../../types/PositionCallsStatus";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
import {
  bankKey,
  liftKey,
  isBankLevelMessage,
  bitmaskPrefix,
  BankKey,
  LiftKey,
} from "../../../utils/typedKeys";
import { bitmaskToDirectionIcon } from "../../../utils/bitmaskToIcon";
import { summarizeBank, bankServiceEntries, BankStateSummary } from "./bankStateSummary";
import { debounceKey, DEVICE_ELEVATOR } from "../../../utils/debounceLookup";

interface State {
  byBank: { [key: BankKey]: SocketMessageInterface };
  byLift: { [key: LiftKey]: SocketMessageInterface };
  /**
   * Last-known bank-level (bank_id === lift_id, see isBankLevelMessage)
   * message per bank, mirroring what `byLift` does for real cars.
   *
   * A bank-level message is deliberately kept OUT of `byLift` (see the
   * UPDATE_MESSAGE branch below), so without a persisted home of its own its
   * hallCalls would only be visible for the single dispatch that happened to
   * carry it: the very next lift-level message recomputes the union from
   * `byLift` alone and has no way to know the bank-level call ever existed,
   * making it disappear one message later instead of when the controller
   * actually reports it answered. This is that home — rollUpBank folds it
   * into the union on EVERY call, not just the one that just updated it.
   */
  byBankLevel: { [key: BankKey]: SocketMessageInterface };
  /**
   * Each bank's REPORTED connectivity, as opposed to the value derived from its
   * lifts.
   *
   * These must not share a home. `byBank[bk].connected` is the OR-rollup the
   * lift branch computes, so reading the bank's verdict back out of it makes
   * the lift branch consume its own output — one lift arriving disconnected
   * would then pin every later lift disconnected too. This map is written only
   * by a bank-level message, which is the only place connectivity is reported.
   */
  bankConnected: { [key: BankKey]: boolean };
  bitmask: { [key: string]: boolean };
  positionMap: { [key: string]: number };
  serviceMap: { [key: string]: string };
  oneHotMap: { [key: string]: any };
  // Per-bank set of every bitmask key ever observed from that bank's CDO.
  // Grows monotonically while the provider is mounted; only CLEAR_MESSAGES
  // resets it. Powers the "CDO Messages" reception-status display.
  everReceived: { [key: BankKey]: Set<string> };
  debounceMap: Map<number, number>;
  last?: SocketMessageInterface;
}

type Action =
  | { type: "UPDATE_MESSAGE"; payload: TerminalRowInterface }
  | { type: "INSPECT_MESSAGE"; payload: TerminalRowInterface }
  | { type: "REPLAY_SEEK"; payload: SocketMessageInterface[] }
  | { type: "UPDATE_DEBOUNCE_MAP"; payload: Map<number, number> }
  | { type: "CLEAR_MESSAGES" };

interface DirectionEntry {
  directionIcon: string | null;
  lastDirectionIcon: string | null;
}

const BankMessagesContext = createContext<State | undefined>(undefined);
const BankMessagesDispatch = createContext<React.Dispatch<Action> | undefined>(undefined);
const DirectionContext = createContext<{ [key: string]: DirectionEntry } | undefined>(undefined);

function mergeEverReceived(
  prev: { [key: BankKey]: Set<string> },
  bk: BankKey,
  bitmaskDelta: { [key: string]: boolean },
): { [key: BankKey]: Set<string> } {
  const keys = Object.keys(bitmaskDelta);
  if (keys.length === 0) return prev;
  const existing = prev[bk];
  const newKeys = existing ? keys.filter((k) => !existing.has(k)) : keys;
  if (newKeys.length === 0) return prev;
  const next = existing ? new Set(existing) : new Set<string>();
  newKeys.forEach((k) => next.add(k));
  return { ...prev, [bk]: next };
}

// ---------------------------------------------------------------------------
// Bank-wide hall-call roll-up
//
// A hall call belongs to the BANK — every car in the group echoes the same
// landing button — so the bank-wide view must be the union of what the
// bank's cars currently report, not whichever car's message happened to be
// dispatched most recently. Without this, a sibling car's unrelated update
// (a position tick, a door event, a direction change) replaces the whole
// byBank entry and makes an active hall call blink out even though it's
// still registered.
//
// Not reusing applyDeltaToState.ts's clonePositionCalls here on purpose: it's
// module-private to the decoder and less null-tolerant than this needs (a
// lift's hallCalls sub-fields can be absent on a sparse/partial message) —
// this reducer intentionally stays decoupled from decoder internals.
// ---------------------------------------------------------------------------
/**
 * The four call arms a PositionCallsStatusT carries, in constructor order.
 *
 * Everything that walks all four arms reads the list from here instead of
 * spelling the names out again, so a fifth arm cannot be added to three of
 * the call sites and quietly forgotten in the fourth.
 */
const CALL_ARMS = ["front", "rear", "frontLock", "rearLock"] as const;

type CallArm = (typeof CALL_ARMS)[number];

type CallSets = Record<CallArm, Set<number>>;

// Spelled out as a literal on purpose: typing it as Record<CallArm, ...> makes
// TypeScript reject this object the moment CALL_ARMS grows, which is a
// stronger guarantee than building it through a helper and casting the result.
const makeCallSets = (): CallSets => ({
  front: new Set<number>(),
  rear: new Set<number>(),
  frontLock: new Set<number>(),
  rearLock: new Set<number>(),
});

/** Add every floor index in `positions` to `acc`, tolerating absent data. */
function collectPositions(acc: Set<number>, positions: number[] | null | undefined): void {
  if (!Array.isArray(positions)) return;
  positions.forEach((position) => {
    if (typeof position === "number") acc.add(position);
  });
}

/** Fold one lift's view of a direction arm (up or down) into the accumulator. */
function mergeArm(acc: CallSets, arm: PositionCallsStatusT | null | undefined): void {
  CALL_ARMS.forEach((field) => collectPositions(acc[field], arm?.[field]));
}

/**
 * Build a PositionCallsStatusT by running `pick` once per call arm.
 *
 * The single place the four arms are threaded into the constructor — both
 * the union (toPositionCalls) and the value-copy (copyPositionCalls) differ
 * only in what `pick` does with each arm, so neither needs its own copy of the
 * four-field walk.
 */
function buildPositionCalls<S>(
  source: S,
  pick: (source: S, arm: CallArm) => number[],
): PositionCallsStatusT {
  const [front, rear, frontLock, rearLock] = CALL_ARMS.map((arm) => pick(source, arm));
  return new PositionCallsStatusT(front, rear, frontLock, rearLock);
}

/** Ascending, duplicate-free — the same canonical order addToArray produces. */
const toSortedPositions = (positions: Set<number>): number[] =>
  Array.from(positions).sort((a, b) => a - b);

const toPositionCalls = (acc: CallSets): PositionCallsStatusT =>
  buildPositionCalls(acc, (sets, field) => toSortedPositions(sets[field]));

const asPositions = (positions: number[] | null | undefined): number[] =>
  Array.isArray(positions) ? [...positions] : [];

/** Value-copy of one arm, normalising absent sub-arrays to empty. */
const copyPositionCalls = (arm: PositionCallsStatusT | null | undefined): PositionCallsStatusT =>
  buildPositionCalls(arm, (source, field) => asPositions(source?.[field]));

/**
 * Bank-wide connectivity + hall-call roll-up, derived from every lift's
 * CURRENT snapshot in `byLift` (never from an independently-accumulated
 * bank-level array — that's what keeps cancellation self-healing: when the
 * last lift reporting a call drops it, the union drops it too).
 *
 * Contributors are the bank's CONNECTED lifts, full stop — no fallback to
 * "every known lift" when none are connected. An earlier version of this
 * function fell back to unioning every lift `byLift` had ever seen for the
 * bank when none were connected, to keep legacy JSON replay (which defaults
 * every record's `connected` to false) from losing multi-lift coverage. That
 * fallback traded one bug for two worse ones: `byLift` is never pruned
 * except by CLEAR_MESSAGES, so it could resurrect an arbitrarily stale call
 * from a lift that had been offline for hours; and the moment any single
 * sibling reconnected, the roll-up would narrow from "every known lift" to
 * "just that one," dropping a still-genuinely-active call from a car that
 * hadn't changed at all — the exact clobber symptom this fix removes, just
 * retriggered by a connectivity transition instead of a heartbeat. Replay's
 * `connected` default was fixed at the source instead (see
 * ReplayFilesPanel.tsx's `normalizeJsonRecord`), so it no longer needs this
 * function to compensate. A bank with zero connected lifts now shows empty
 * hall calls, matching its `connected: false` flag honestly rather than
 * guessing from whatever stale data happens to still be sitting in `byLift`.
 *
 * `upAssigned` / `downAssigned` are deliberately NOT unioned — an assigned
 * call names the one car dispatched to answer it, so a bank-wide OR would
 * claim every car is assigned to every call. They pass through from the
 * dispatching message only, so they are still subject to today's
 * last-dispatcher-wins behavior (same as direction/doorStatus/position/
 * serviceMode/faults/carCalls — see the reducer's lift-level branch). That's
 * fine today because every real consumer of those two fields reads the
 * per-LIFT message, never the bank message — but it means adding a bank-wide
 * consumer of them later would need this function extended first, not
 * assumed to already cover it.
 *
 * That caveat was load-bearing: the State tab's bank card WAS such a
 * consumer, and read direction, position, doors, service mode and faults
 * straight off this entry, showing whichever car dispatched last under the
 * bank's name. The fix was NOT to extend this function — widening `byBank`
 * would change what its four consumers see, and there is no value to widen
 * `direction` or `position` TO. The card now reads `useBankStateSummary`
 * instead, which aggregates from `byLift` and omits the fields a bank has no
 * value for. See `bankStateSummary.ts` for the per-field reasoning. The rule
 * above therefore still stands, unchanged: anything wanting a bank-wide view
 * of a per-car field must derive it there, not assume this entry carries it.
 */
function rollUpBank(
  byLift: { [key: LiftKey]: SocketMessageInterface },
  bk: BankKey,
  msg: SocketMessageInterface,
  bankLevelEntry?: SocketMessageInterface,
): { connected: boolean; hallCalls: HallCallsState } {
  const up = makeCallSets();
  const down = makeCallSets();
  let connected = false;

  (Object.values(byLift) as SocketMessageInterface[]).forEach((lift) => {
    if (bankKey(lift.bankId) !== bk || !lift.connected) return;
    connected = true;
    mergeArm(up, lift.hallCalls?.up);
    mergeArm(down, lift.hallCalls?.down);
  });

  // Fold in the bank's PERSISTED bank-level (bank_id === lift_id) snapshot,
  // if any — see `byBankLevel` on State for why this has to be threaded
  // through explicitly rather than re-derived from `msg`. `msg` is only the
  // bank-level message itself on the one dispatch that produced it; every
  // subsequent lift-level dispatch calls this with the SAME persisted
  // `bankLevelEntry` so the call keeps showing until the controller reports
  // it answered (a new bank-level message with `active: false`), not until
  // the next unrelated message happens to arrive.
  //
  // Gated on its own `connected`, same as a real lift above — a bank-level
  // entry reported as disconnected should stop contributing, not keep a
  // stale call lit forever.
  if (bankLevelEntry && bankKey(bankLevelEntry.bankId) === bk && bankLevelEntry.connected) {
    connected = true;
    mergeArm(up, bankLevelEntry.hallCalls?.up);
    mergeArm(down, bankLevelEntry.hallCalls?.down);
  }

  return {
    connected,
    hallCalls: {
      up: toPositionCalls(up),
      down: toPositionCalls(down),
      upAssigned: copyPositionCalls(msg.hallCalls?.upAssigned),
      downAssigned: copyPositionCalls(msg.hallCalls?.downAssigned),
    },
  };
}

/**
 * Every field of a freshly-mounted provider except `debounceMap`, which is
 * configuration fetched once rather than accumulated message state and so
 * survives both CLEAR_MESSAGES and a replay rebuild.
 */
const pristineState = (): Omit<State, "debounceMap"> => ({
  byBank: {},
  byLift: {},
  byBankLevel: {},
  bankConnected: {},
  bitmask: {},
  positionMap: {},
  serviceMap: {},
  oneHotMap: {},
  everReceived: {},
  last: undefined,
});

/**
 * A Terminal row that carries `txMeta` is a UiCmd WE sent, echoed back into
 * the table for display — not controller state. It reports `connected: false`
 * and empty `hallCalls` because a UiCmd carries neither, so folding one into
 * `byLift` silently drops that lift from the bank's hall-call union and can
 * flip the whole bank to disconnected. The absence of `txMeta` IS the "this
 * came from the controller" signal (see `TerminalRowInterface`), so that is
 * the discriminant used here — never `instanceof`, which does not survive the
 * structuredClone and Redux round-trips these rows already make.
 */
const isOutboundRow = (msg: TerminalRowInterface): boolean => msg.txMeta !== undefined;

/**
 * Push a bank's connectivity verdict onto every lift it owns.
 *
 * The controller reports connectivity for the bank, never per lift, so the
 * bank's answer IS each lift's answer. Returns the original map when nothing
 * changes, so an unchanged verdict cannot cause a re-render.
 */
function applyBankConnectivity(
  byLift: { [key: LiftKey]: SocketMessageInterface },
  bk: BankKey,
  connected: boolean,
): { [key: LiftKey]: SocketMessageInterface } {
  const keys = (Object.keys(byLift) as LiftKey[]).filter(
    (k) => bankKey(byLift[k].bankId) === bk && byLift[k].connected !== connected,
  );

  if (keys.length === 0) return byLift;

  const next = { ...byLift };
  keys.forEach((k) => {
    next[k] = { ...next[k], connected };
  });

  return next;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "UPDATE_MESSAGE": {
      const msg = action.payload;
      if (isOutboundRow(msg)) return state;
      const bk = bankKey(msg.bankId);
      const mergedMaps = {
        bitmask: { ...state.bitmask, ...msg.bitmask },
        positionMap: { ...state.positionMap, ...msg.positionMap },
        serviceMap: { ...state.serviceMap, ...msg.serviceMap },
        oneHotMap: { ...state.oneHotMap, ...msg.oneHotMap },
      };
      const everReceived = mergeEverReceived(state.everReceived, bk, msg.bitmask);

      // When bank_id === lift_id the message applies to the bank as a whole,
      // not to any individual lift (project convention: never filter these
      // out — VIP/group-level signals are real, legitimate traffic on this
      // path, not noise). Store it only in byBank (and byBankLevel, below),
      // never in byLift — so it never contributes to the cross-lift hall-call
      // roll-up above via the normal per-lift path. `byBankLevel` is what
      // keeps this message's hallCalls alive for every dispatch AFTER this
      // one too, not just this one — see byBankLevel's doc comment on State
      // for why that persistence is required at all.
      if (isBankLevelMessage(msg.bankId, msg.liftId)) {
        const liftsAfterBankVerdict = applyBankConnectivity(state.byLift, bk, msg.connected);
        const nextByBankLevel = { ...state.byBankLevel, [bk]: msg };
        // Recompute the union from the lifts rather than preserving the previous
        // one. The verdict just changed which lifts count, so the old union is
        // stale by construction: a bank going offline has to empty the strip,
        // and a bank coming back has to refill it. Preserving it was correct
        // when this branch could not affect connectivity; now it can.
        const bankWideCalls = rollUpBank(liftsAfterBankVerdict, bk, msg, msg).hallCalls;

        return {
          byBank: {
            ...state.byBank,
            [bk]: { ...msg, hallCalls: bankWideCalls },
          },
          // Connectivity is a property of the BANK, and the controller only ever
          // reports it at bank level. So the bank's verdict is pushed down onto
          // every lift it owns: when the bank is connected all its lifts are,
          // and when it is not, none of them are.
          //
          // Without this the flag was one-way. A lift could only be marked
          // disconnected, never reconnected, because the message that would say
          // otherwise arrives addressed to the bank and this branch left byLift
          // untouched. rollUpBank drops a disconnected lift from the hall-call
          // union, so one such message emptied the bank's hall-call strip for
          // the rest of the session -- until a WebSocket reconnect reset the
          // defaults, which is why it looked like the strip came back and then
          // vanished again.
          byLift: liftsAfterBankVerdict,
          byBankLevel: nextByBankLevel,
          bankConnected: { ...state.bankConnected, [bk]: msg.connected },
          ...mergedMaps,
          everReceived,
          debounceMap: state.debounceMap,
          last: msg,
        };
      }

      // Lift-level message — store in byLift, then derive the bank-wide view
      // (connectivity OR + hall-call union) from every lift's CURRENT
      // snapshot. See rollUpBank's doc comment for the full rationale.
      const lk = liftKey(msg.liftId);
      // Connectivity belongs to the BANK and is ONLY ever reported there, so a
      // per-lift frame carries no verdict of its own and none is invented for
      // it. A lift the bank has not spoken about is disconnected, which is the
      // documented starting state; `bankConnected` is written only by a
      // bank-level Connected message.
      //
      // Deliberately NOT `?? state.byLift[lk]?.connected ?? msg.connected`.
      // That middle arm reads as a fallback but behaves as a latch: once a lift
      // has any entry at all, its previous value always answers before
      // `msg.connected` is consulted, so the flag freezes at whatever the FIRST
      // frame happened to say and nothing can move it while the bank stays
      // silent. The tell is that the third arm becomes unreachable.
      //
      // With the bank silent the message's own value is the only evidence there
      // is, so it is used. Once the bank has spoken -- in either direction --
      // its answer overrides every lift, which is the rule the controller
      // actually implements.
      const updatedByLift = {
        ...state.byLift,
        [lk]: {
          ...msg,
          connected: state.bankConnected[bk] ?? msg.connected,
        },
      };
      const { connected: bankConnected, hallCalls: bankHallCalls } = rollUpBank(
        updatedByLift,
        bk,
        msg,
        state.byBankLevel[bk],
      );
      return {
        byBank: {
          ...state.byBank,
          [bk]: { ...msg, connected: bankConnected, hallCalls: bankHallCalls },
        },
        byLift: updatedByLift,
        byBankLevel: state.byBankLevel,
        bankConnected: state.bankConnected,
        ...mergedMaps,
        everReceived,
        debounceMap: state.debounceMap,
        last: msg,
      };
    }
    // Show one historical Terminal row on the dashboard without folding it
    // into anything derived across lifts or across time.
    //
    // Clicking a row while the Terminal is frozen is meant to put THAT record
    // in front of the operator, and in split view the dashboard pane is right
    // beside the table. Routing the click through UPDATE_MESSAGE made it do
    // considerably more than that: it overwrote `byLift[lift]` with a snapshot
    // from an arbitrary point in the past, and `byBank` is derived from
    // `byLift`, so the very next roll-up unioned that stale car against its
    // siblings' live snapshots and produced a bank state that never existed on
    // the wire. It also merged the row's CDO keys into the four monotonic maps
    // and into `everReceived`, which only CLEAR_MESSAGES can undo.
    //
    // So: update the bank entry's passthrough fields (position, direction,
    // doorStatus, serviceMode, faults, carCalls — the ones that are already
    // last-writer-wins by design) and `last`, and leave `byLift`, the four
    // maps and `everReceived` exactly as they were. `connected` and
    // `hallCalls` are the two genuinely bank-wide, cross-lift-derived fields,
    // so they are carried over from the existing bank entry rather than taken
    // from a single historical row — the same treatment the bank-level-message
    // branch above gives them, for the same reason.
    case "INSPECT_MESSAGE": {
      const msg = action.payload;
      if (isOutboundRow(msg)) return state;
      const bk = bankKey(msg.bankId);
      const previous = state.byBank[bk];
      return {
        ...state,
        byBank: {
          ...state.byBank,
          [bk]: previous
            ? { ...msg, connected: previous.connected, hallCalls: previous.hallCalls }
            : msg,
        },
        last: msg,
      };
    }
    // Rebuild the whole fold from the start of the replay list.
    //
    // A replay scrub that is not "the record straight after the last one" —
    // stepping back, wrapping from the last record to the first, the first
    // dispatch after the filter changed — cannot be expressed as a patch.
    // `byLift`, the four CDO maps and `everReceived` all accumulate
    // monotonically, so after jumping backwards the bank roll-up would still
    // be unioning lift snapshots taken from records that are now in the
    // future, and no later message would ever clear them: `byLift` is pruned
    // by nothing but CLEAR_MESSAGES.
    //
    // Refolding the prefix through the SAME UPDATE_MESSAGE branch (rather
    // than a second implementation of it) is what makes a seek to index N
    // indistinguishable from having played 0..N straight through, and is why
    // the incremental and rebuilt paths cannot drift apart.
    case "REPLAY_SEEK": {
      return action.payload.reduce<State>(
        (acc, msg) => reducer(acc, { type: "UPDATE_MESSAGE", payload: msg }),
        { ...pristineState(), debounceMap: state.debounceMap },
      );
    }
    case "UPDATE_DEBOUNCE_MAP": {
      return { ...state, debounceMap: action.payload };
    }
    case "CLEAR_MESSAGES": {
      return { ...pristineState(), debounceMap: state.debounceMap };
    }
    default:
      return state;
  }
}

export const BankMessagesProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, {
    ...pristineState(),
    debounceMap: new Map(),
  });

  const [directionByLift, setDirectionByLift] = useState<{ [key: string]: DirectionEntry }>({});
  const debouncedDirRef = useRef<{ [key: string]: string | null }>({});
  const lastNonNullDirRef = useRef<{ [key: string]: string | null }>({});
  const timersRef = useRef<{ [key: string]: ReturnType<typeof setTimeout> }>({});

  useEffect(() => {
    if (!state.last) return;
    const msg = state.last;
    const lk = liftKey(msg.liftId);
    const prefix = bitmaskPrefix(msg.bankId, msg.liftId);
    const rawDirection = bitmaskToDirectionIcon(msg.bitmask, prefix);
    const currentDebounced = debouncedDirRef.current[lk] ?? null;

    if (rawDirection === currentDebounced) return;

    let debounceMs = 0;
    if (rawDirection === "elUp") {
      debounceMs =
        state.debounceMap.get(
          debounceKey(DEVICE_ELEVATOR, MessageContent.Usage_UsageMessage, Usage.Up),
        ) ?? 0;
    } else if (rawDirection === "elDown") {
      debounceMs =
        state.debounceMap.get(
          debounceKey(DEVICE_ELEVATOR, MessageContent.Usage_UsageMessage, Usage.Down),
        ) ?? 0;
    }

    if (timersRef.current[lk]) {
      clearTimeout(timersRef.current[lk]);
      delete timersRef.current[lk];
    }

    const apply = () => {
      debouncedDirRef.current[lk] = rawDirection;
      if (rawDirection) lastNonNullDirRef.current[lk] = rawDirection;
      const lastDir = lastNonNullDirRef.current[lk] ?? null;
      setDirectionByLift((prev) => {
        const next: DirectionEntry = {
          directionIcon: rawDirection,
          lastDirectionIcon: rawDirection ?? lastDir,
        };
        if (
          prev[lk]?.directionIcon === next.directionIcon &&
          prev[lk]?.lastDirectionIcon === next.lastDirectionIcon
        ) {
          return prev;
        }
        return { ...prev, [lk]: next };
      });
    };

    if (debounceMs <= 0) {
      apply();
    } else {
      timersRef.current[lk] = setTimeout(() => {
        delete timersRef.current[lk];
        apply();
      }, debounceMs);
    }
  }, [state.last, state.debounceMap]);

  useEffect(
    () => () => {
      Object.values(timersRef.current).forEach((timer) => clearTimeout(timer));
    },
    [],
  );

  return (
    <BankMessagesContext.Provider value={state}>
      <BankMessagesDispatch.Provider value={dispatch}>
        <DirectionContext.Provider value={directionByLift}>{children}</DirectionContext.Provider>
      </BankMessagesDispatch.Provider>
    </BankMessagesContext.Provider>
  );
};

export function useLastBankMessage(bankId: UuidT): SocketMessageInterface | undefined {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.byBank[bankKey(bankId)]);
}

export function useLastLiftMessage(liftId: UuidT): SocketMessageInterface | undefined {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.byLift[liftKey(liftId)]);
}

export function useAllLiftMessages(): {
  [key: string]: SocketMessageInterface;
} {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.byLift) || {};
}

export function useLastSocketMessage(): SocketMessageInterface | undefined {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.last);
}

export function useBankDispatch() {
  return useContextSelector(BankMessagesDispatch, (dispatch) => {
    if (!dispatch) throw new Error("useBankDispatch must be used within BankMessagesProvider");
    return dispatch;
  });
}

export function useBitmask(): { [key: string]: boolean } {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.bitmask) || {};
}

export function usePositionMap(): { [key: string]: number } {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.positionMap) || {};
}

export function useServiceMap(): { [key: string]: string } {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.serviceMap) || {};
}

export function useDebounceMap(): Map<number, number> {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.debounceMap) || new Map();
}

export function useClearBankMessages() {
  const dispatch = useBankDispatch();
  return () => dispatch({ type: "CLEAR_MESSAGES" });
}

export function useOneHotMap(): { [key: string]: any } {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.oneHotMap) || {};
}

const EMPTY_EVER_RECEIVED: ReadonlySet<string> = new Set();

/**
 * Honest bank-wide summary for the State tab's "Bank" card, derived from every
 * connected lift of the bank. See `bankStateSummary.ts` for what is aggregated
 * and — just as importantly — what deliberately is not.
 *
 * Deliberately NOT folded into `byBank`. That entry is a
 * `SocketMessageInterface` read by four other consumers, and giving its
 * per-car fields blanked values would claim "Idle"/"floor 0"/"closed" rather
 * than admitting the bank has no such value. Keeping the summary in its own
 * type means a caller that needs a car's direction has to go to `byLift` and
 * name the car, which is the only honest way to get one.
 */
export function useBankStateSummary(bankId: UuidT): BankStateSummary {
  const byLift = useContextSelector(BankMessagesContext, (ctx) => ctx?.byLift);
  const bk = bankKey(bankId);
  return useMemo(() => summarizeBank(byLift ?? {}, bk), [byLift, bk]);
}

/**
 * Every service-map entry belonging to this bank — a per-lift listing for the
 * Service Map section, not a roll-up. Scoped to the bank so the card stops
 * showing only whichever car dispatched last.
 */
export function useBankServiceEntries(bankId: UuidT): { [key: string]: string } {
  const byLift = useContextSelector(BankMessagesContext, (ctx) => ctx?.byLift);
  const bk = bankKey(bankId);
  return useMemo(() => bankServiceEntries(byLift ?? {}, bk), [byLift, bk]);
}

/**
 * Bitmask keys ever observed for the given bank since this provider mounted.
 * Used by the State tab's "CDO Messages" section so a key shows as grey when
 * we know the CDO emits it but the bitmask no longer carries a current value.
 */
export function useEverReceivedForBank(bankId: UuidT): ReadonlySet<string> {
  return (
    useContextSelector(BankMessagesContext, (ctx) => ctx?.everReceived[bankKey(bankId)]) ??
    EMPTY_EVER_RECEIVED
  );
}

export function useDirectionForLift(liftId: UuidT): DirectionEntry {
  const lk = liftKey(liftId);
  return (
    useContextSelector(DirectionContext, (ctx) => ctx?.[lk]) ?? {
      directionIcon: null,
      lastDirectionIcon: null,
    }
  );
}
